import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateContentItem } from '~/api/content-items';
import { AppForm, type AppFormSubmitProps } from '~/components/AppForm';
import { SelectFolderDialog } from '~/components/dialogs/SelectContentFolder';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { useLocation } from '~/hooks/location';
import { assert } from '~/lib/assert';

export const CreateTemplate = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const link = useLink();
  const [createApp] = useCreateContentItem();
  const { templateId } = useParams<{ templateId: string }>();

  const [selectFolderOpen, setSelectFolderOpen] = useState(false);

  const [mutationState, setMutationState] = useState<AppFormSubmitProps>();

  const handleSubmit = useCallback(
    async (folderId: string) => {
      assert(mutationState != null, 'New template: no mutation state');

      const { patch, name, templateVersionId } = mutationState;

      assert(name != null, 'New template: no name provided');
      assert(templateVersionId != null, 'New template: no templateVersionId provided');

      const { data } = await createApp({
        variables: {
          input: {
            ...patch,
            contentFolderId: +folderId,
            networkId: currentNetwork.id,
            name,
            templateVersionId,
          },
        },
      });

      if (data?.createContentItem?.contentItem?.id) {
        const appId = data.createContentItem.contentItem.id;
        const returnTo = location.state?.returnTo || location.fullPath;
        navigate(
          link(
            `/content/${data.createContentItem.contentItem.contentFolderId}/apps/${appId}/settings`,
          ),
          {
            state: { returnTo },
          },
        );
      }

      setSelectFolderOpen(false);
    },
    [createApp, currentNetwork.id, link, location, mutationState, navigate],
  );

  const selectFolder = useCallback(({ patch, name, templateVersionId }: AppFormSubmitProps) => {
    setSelectFolderOpen(true);

    setMutationState({ patch, name, templateVersionId });

    return Promise.resolve();
  }, []);

  return (
    <>
      <AppForm getApp displayActions={false} id={Number(templateId)} onSubmit={selectFolder} />
      <SelectFolderDialog
        description={t('pages.templates.create.select_folder_description')}
        open={selectFolderOpen}
        onClose={() => setSelectFolderOpen(false)}
        onSubmit={handleSubmit}
      />
    </>
  );
};
